import React, { useEffect, useState } from 'react';
import { Chip } from '../../lib/types';
import { US_STATES } from '../../lib/utils';
import { useToast } from '../../components/Toast/ToastContext';
import Modal, { ModalTitle, ModalSubtitle, ModalActions } from '../../components/Modal/Modal';
import StepProgress from '../../components/StepProgress/StepProgress';
import Button from '../../components/Button/Button';
import { PortalInput } from '../../components/FormField/PortalInput';
import { PortalSelect } from '../../components/FormField/PortalSelect';
import { PortalField } from '../../components/FormField/PortalField';
import { FormGrid } from '../../components/FormField/FormGrid';
import ConfirmSummary from '../../components/ConfirmSummary/ConfirmSummary';
import styles from './Chips.module.scss';

export type DeviceTransferAction = 'transfer' | 'return';

/**
 * Thrown by `onTransfer` when the device-side mutation succeeded but the chip
 * owner assignment that followed failed. The modal treats this as a
 * non-retryable stuck state — re-running the device mutation would either
 * create a duplicate transfer or hit a "device already unassigned" error — so
 * the user is told to contact support instead of being allowed to retry.
 */
export class DeviceAppliedChipFailedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DeviceAppliedChipFailedError';
  }

  /**
   * Standard message format used by both the Chips page and Dashboard
   * Add Owner handlers. Keeping the wording in one place so the two
   * call sites don't drift.
   */
  static fromDeviceAction(deviceDescription: string, detail: string): DeviceAppliedChipFailedError {
    return new DeviceAppliedChipFailedError(
      `${deviceDescription}, but assigning the new chip owner failed (${detail}). ` +
        `Don't retry from this dialog — contact support to finish the owner assignment.`,
    );
  }
}

interface TransferChipModalProps {
  open: boolean;
  onClose: () => void;
  chip: Chip | null;
  /**
   * Optional GPS device currently held by the rescue for this chip's pet. When
   * present, an extra step is inserted before the confirmation step that asks
   * how to handle the device, and the chosen action is passed to onTransfer.
   */
  device?: { moduleId: string } | null;
  /**
   * Called when the user clicks Add Owner on the final confirmation step. The
   * implementer is responsible for performing the chip mutation (and, when
   * `deviceAction` is provided, the matching device mutation) atomically. The
   * modal awaits this promise, shows the success toast, and closes only once
   * it resolves. If it rejects with a generic error the modal stays open so
   * the user can retry without re-entering data. If it rejects with a
   * `DeviceAppliedChipFailedError` the modal locks into a stuck state: the
   * device mutation has already been applied, so retrying would compound the
   * problem; the user is shown the error and Confirm/Back are disabled.
   */
  onTransfer: (chipId: string, adopter: AdopterFormData, deviceAction?: DeviceTransferAction) => Promise<void> | void;
}

export interface AdopterFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  street: string;
  apt: string;
  city: string;
  state: string;
  zip: string;
}

export const INITIAL_ADOPTER: AdopterFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  street: '',
  apt: '',
  city: '',
  state: '',
  zip: '',
};

const DEFAULT_DEVICE_ACTION: DeviceTransferAction = 'transfer';

export default function TransferChipModal({ open, onClose, chip, device, onTransfer }: TransferChipModalProps) {
  const { showToast } = useToast();
  const [step, setStep] = useState(1);
  const [adopter, setAdopter] = useState<AdopterFormData>({ ...INITIAL_ADOPTER });
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [deviceAction, setDeviceAction] = useState<DeviceTransferAction>(DEFAULT_DEVICE_ACTION);
  const [submitting, setSubmitting] = useState(false);
  /**
   * Set when the device mutation has already been applied to the backend but
   * the follow-up chip owner assignment failed. The modal stays open but
   * Confirm and Back are disabled so the user can't compound the partial
   * failure by retrying.
   */
  const [stuckError, setStuckError] = useState<string | null>(null);
  /**
   * Snapshot of the `device` prop captured when the modal opens. The step
   * counts and which branch renders are derived from this, not the live prop,
   * so a parent re-render with `device=null` mid-flow can't blank out step 3.
   * Reset when the modal closes so the next open picks up the latest value.
   */
  const [snapshotDevice, setSnapshotDevice] = useState<{ moduleId: string } | null>(device ?? null);
  useEffect(() => {
    if (open) {
      setSnapshotDevice(device ?? null);
    } else {
      setSnapshotDevice(null);
    }
    // `device` is intentionally excluded — we only want to capture on open
    // transition, not on every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const hasDeviceStep = !!snapshotDevice;
  const totalSteps = hasDeviceStep ? 3 : 2;
  const confirmStep = totalSteps;
  const deviceStep = hasDeviceStep ? 2 : null;
  const isStuck = stuckError !== null;

  function reset() {
    setStep(1);
    setAdopter({ ...INITIAL_ADOPTER });
    setErrors({});
    setDeviceAction(DEFAULT_DEVICE_ACTION);
    setSubmitting(false);
    setStuckError(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  function updateAdopter<K extends keyof AdopterFormData>(key: K, value: string) {
    setAdopter((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  function validateAdopter(): boolean {
    const errs: Record<string, boolean> = {};
    if (!adopter.firstName.trim()) errs.firstName = true;
    if (!adopter.lastName.trim()) errs.lastName = true;
    if (!adopter.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adopter.email)) errs.email = true;
    if (!adopter.phone.trim()) errs.phone = true;
    if (!adopter.street.trim()) errs.street = true;
    if (!adopter.city.trim()) errs.city = true;
    if (!adopter.state) errs.state = true;
    if (!adopter.zip.trim() || !/^\d{5}$/.test(adopter.zip)) errs.zip = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleAdopterContinue() {
    if (!validateAdopter()) return;
    setStep(deviceStep ?? confirmStep);
  }

  async function handleConfirm() {
    if (!chip || submitting || isStuck) return;
    setSubmitting(true);
    try {
      await onTransfer(chip.id, adopter, hasDeviceStep ? deviceAction : undefined);
    } catch (err) {
      setSubmitting(false);
      if (err instanceof DeviceAppliedChipFailedError) {
        // Device mutation succeeded but chip mutation failed. Retrying would
        // re-fire the device mutation (likely duplicate-transfer error or
        // "device not assigned" error). Lock the modal and tell the user.
        setStuckError(err.message);
        return;
      }
      // Generic failure — caller surfaces the toast; keep modal open so the
      // user can retry without re-entering data.
      return;
    }
    showToast(`Owner added to ${chip.id}`);
    handleClose();
  }

  if (!chip) return null;

  const fullAddress = [
    adopter.street,
    adopter.apt ? `Apt ${adopter.apt}` : '',
    `${adopter.city}, ${adopter.state} ${adopter.zip}`,
  ]
    .filter(Boolean)
    .join(', ');

  const adopterFullName = `${adopter.firstName} ${adopter.lastName}`.trim();
  const petName = chip.petName ?? 'this pet';

  const deviceOptions: { value: DeviceTransferAction; title: string; description: string }[] = [
    {
      value: 'transfer',
      title: 'Transfer device to adopter',
      description: `Initiate adoption transfer to ${adopterFullName || 'the new owner'} with the same contact info`,
    },
    {
      value: 'return',
      title: 'Return device to rescue',
      description: `Remove device from ${petName} and return to your unassigned stock`,
    },
  ];

  const confirmSections: { title: string; fields: { label: string; value: string | null | undefined }[] }[] = [
    {
      title: 'Pet',
      fields: [
        { label: 'Chip ID', value: chip.id },
        { label: 'Pet Name', value: chip.petName },
        { label: 'Species', value: chip.species },
        { label: 'Breed', value: chip.breed },
      ],
    },
    {
      title: 'New Owner',
      fields: [
        { label: 'Name', value: adopterFullName },
        { label: 'Email', value: adopter.email },
        { label: 'Phone', value: adopter.phone },
        { label: 'Address', value: fullAddress },
      ],
    },
  ];
  if (hasDeviceStep && snapshotDevice) {
    confirmSections.push({
      title: 'GPS Device',
      fields: [
        { label: 'Device', value: snapshotDevice.moduleId },
        {
          label: 'Action',
          value: deviceAction === 'transfer' ? 'Transfer device to adopter' : 'Return device to rescue',
        },
        {
          label: 'Details',
          value:
            deviceAction === 'transfer'
              ? `Initiate adoption transfer to ${adopterFullName || 'the new owner'} with the same contact info`
              : `Remove device from ${petName} and return to your unassigned stock`,
        },
      ],
    });
  }

  return (
    <Modal open={open} onClose={handleClose} size="wide">
      <StepProgress currentStep={step} totalSteps={totalSteps} />

      {/* Step 1: Adopter Info */}
      {step === 1 && (
        <div>
          <ModalTitle>Add owner</ModalTitle>
          <p className={styles.modalDescription}>Enter the owner&apos;s contact information to assign this chip.</p>
          <FormGrid>
            <PortalField label="First Name *" error="Required" showError={errors.firstName}>
              <PortalInput
                value={adopter.firstName}
                onChange={(e) => updateAdopter('firstName', e.target.value)}
                placeholder="First name"
                hasError={errors.firstName}
              />
            </PortalField>

            <PortalField label="Last Name *" error="Required" showError={errors.lastName}>
              <PortalInput
                value={adopter.lastName}
                onChange={(e) => updateAdopter('lastName', e.target.value)}
                placeholder="Last name"
                hasError={errors.lastName}
              />
            </PortalField>

            <PortalField label="Email *" error="Valid email required" showError={errors.email}>
              <PortalInput
                value={adopter.email}
                onChange={(e) => updateAdopter('email', e.target.value)}
                placeholder="Email address"
                type="email"
                hasError={errors.email}
              />
            </PortalField>

            <PortalField label="Phone *" error="Required" showError={errors.phone}>
              <PortalInput
                value={adopter.phone}
                onChange={(e) => updateAdopter('phone', e.target.value)}
                placeholder="Phone number"
                type="tel"
                hasError={errors.phone}
              />
            </PortalField>

            <PortalField label="Street Address *" error="Required" showError={errors.street} fullWidth>
              <PortalInput
                value={adopter.street}
                onChange={(e) => updateAdopter('street', e.target.value)}
                placeholder="Street address"
                hasError={errors.street}
              />
            </PortalField>

            <PortalField label="Apt / Suite">
              <PortalInput
                value={adopter.apt}
                onChange={(e) => updateAdopter('apt', e.target.value)}
                placeholder="Apt / Suite"
              />
            </PortalField>

            <PortalField label="City *" error="Required" showError={errors.city}>
              <PortalInput
                value={adopter.city}
                onChange={(e) => updateAdopter('city', e.target.value)}
                placeholder="City"
                hasError={errors.city}
              />
            </PortalField>

            <PortalField label="State *" error="Required" showError={errors.state}>
              <PortalSelect
                value={adopter.state}
                onChange={(e) => updateAdopter('state', e.target.value)}
                hasError={errors.state}
              >
                <option value="">Select state</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </PortalSelect>
            </PortalField>

            <PortalField label="Zip Code *" error="5-digit zip required" showError={errors.zip}>
              <PortalInput
                value={adopter.zip}
                onChange={(e) => updateAdopter('zip', e.target.value)}
                placeholder="Zip code"
                hasError={errors.zip}
              />
            </PortalField>
          </FormGrid>

          <ModalActions>
            <Button variant="green" onClick={handleAdopterContinue}>
              Continue
            </Button>
          </ModalActions>
        </div>
      )}

      {/* Step 2 (when device present): GPS device handling */}
      {step === deviceStep && snapshotDevice && (
        <div>
          <ModalTitle>Transfer {petName}&apos;s Microchip</ModalTitle>
          <ModalSubtitle>
            Chip {chip.fullChipId}
            {chip.breed ? ` · ${chip.breed}` : chip.species ? ` · ${chip.species}` : ''}
          </ModalSubtitle>

          <div className={styles.deviceTransferCard}>
            <h3 className={styles.deviceTransferCardTitle}>
              <span className={styles.deviceTransferCardIcon} aria-hidden="true">
                ◉
              </span>
              This pet also has a GPS device
            </h3>
            <p className={styles.deviceTransferCardBody}>
              {petName} has GPS device <strong>{snapshotDevice.moduleId}</strong> currently held by your rescue. What
              would you like to do with the device?
            </p>

            <div className={styles.ownerChoiceGroup} role="radiogroup" aria-label="Device handling">
              {deviceOptions.map((option) => {
                const active = deviceAction === option.value;
                return (
                  <label
                    key={option.value}
                    className={`${styles.ownerChoiceCard} ${active ? styles.ownerChoiceCardActive : ''}`}
                  >
                    <input
                      type="radio"
                      name="deviceAction"
                      value={option.value}
                      checked={active}
                      onChange={() => setDeviceAction(option.value)}
                      className={styles.ownerChoiceInput}
                    />
                    <span className={styles.ownerChoiceRadio} aria-hidden="true" />
                    <span className={styles.ownerChoiceContent}>
                      <span className={styles.ownerChoiceTitle}>{option.title}</span>
                      <span className={styles.ownerChoiceDescription}>{option.description}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <ModalActions>
            <Button variant="secondary" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button variant="green" onClick={() => setStep(confirmStep)}>
              Continue
            </Button>
          </ModalActions>
        </div>
      )}

      {/* Final step: Confirm */}
      {step === confirmStep && (
        <div>
          <ModalTitle>Confirm owner</ModalTitle>
          <p className={styles.modalDescription}>Review the details below before assigning the owner.</p>
          <ConfirmSummary sections={confirmSections} />
          {stuckError && (
            <div className={styles.transferStuckError} role="alert">
              <strong>Action partially applied.</strong>
              <span>{stuckError}</span>
            </div>
          )}
          <ModalActions>
            <Button variant="secondary" onClick={() => setStep(deviceStep ?? 1)} disabled={submitting || isStuck}>
              Back
            </Button>
            {isStuck ? (
              <Button variant="secondary" onClick={handleClose}>
                Close
              </Button>
            ) : (
              <Button variant="green" onClick={handleConfirm} disabled={submitting}>
                Add Owner
              </Button>
            )}
          </ModalActions>
        </div>
      )}
    </Modal>
  );
}
